/*
 * UK / EU / US tabs on a footwear size filter.
 *
 * A footwear collection arrives from Shopify with both systems flat in one
 * list, because vendors label in whichever system they use and Shopify simply
 * reports the option values it finds. On /collections/footwear that is
 * thirty-six entries running "EU 35 … EU 47, UK 2.5 … UK 15", and a customer
 * looking for a nine has to know that part of the stock calls it forty-three
 * and tick both. Most people do not: UK 9 alone is 371 products and the EU 43
 * beside it is another 113 they never see.
 *
 * So the list is folded into one row per foot, using the shop's own brand
 * conversion maps — the same ones the apps use, asked for over the proxy
 * rather than reimplemented here, so the website and the apps cannot come to
 * disagree about which UK size an EU 43 is.
 *
 * The original checkboxes are not replaced. They are hidden and left exactly
 * where Liquid put them, and a rung ticks every one that means the same foot.
 * That matters: the filter form, the no-reload swap, the active-filter chips
 * and the URL all keep working without knowing this file exists, and with
 * JavaScript off the shopper gets the plain list rather than nothing at all.
 */
(function () {
  var ENDPOINT = '/apps/tru/size-groups';
  var SYSTEM_NAMES = { uk: 'UK', eu: 'EU', us: 'US' };

  /*
   * Which tab to open on.
   *
   * A shopper in Madrid should not have to know their UK size to use a
   * British shop, which is the whole complaint. The country is stamped on
   * <html> by the section from Shopify's own localisation, so this is where
   * the customer is being served, not where they happen to be browsing from.
   */
  function homeSystem() {
    var country = (document.documentElement.getAttribute('data-tc-country') || '').toUpperCase();
    if (!country) return 'uk';
    if (country === 'GB' || country === 'IE') return 'uk';
    if (country === 'US' || country === 'CA') return 'us';
    return 'eu';
  }

  /* One request per set of labels, not per render: the grid is swapped on
     every filter tick and the answer for a given list never changes. */
  var pending = {};

  function fetchGrouping(values) {
    var key = values.map(function (v) { return v.label; }).join('|');
    if (!pending[key]) {
      pending[key] = fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ values: values }),
      })
        .then(function (r) { return r.ok ? r.json() : { grouping: null }; })
        .then(function (d) { return d.grouping; })
        .catch(function () { return null; });
    }
    return pending[key];
  }

  /** The size filter on the page, if it has not been folded already. */
  function findSizeGroup(root) {
    var groups = root.querySelectorAll('.tru-filter-group');
    for (var i = 0; i < groups.length; i++) {
      var name = groups[i].querySelector('summary span');
      if (!name || !/size/i.test(name.textContent)) continue;
      if (groups[i].hasAttribute('data-tc-size-tabs')) continue;
      return groups[i];
    }
    return null;
  }

  /** The labels as they stand, and the checkboxes they belong to. */
  function readValues(group) {
    var boxes = group.querySelectorAll('input[type="checkbox"][data-tru-filter-input]');
    var values = [];
    for (var i = 0; i < boxes.length; i++) {
      var row = boxes[i].closest('label');
      var text = (row ? row.textContent : '').replace(/\s+/g, ' ').trim();
      /* The count sits in the same row as the name. Only the name is parsed
         for a size, or "UK 9 371" would be read as a size of nine hundred. */
      var size = text.match(/^((?:UK|EU|US)\s*[\d.]+)/i);
      if (!size) continue;
      var count = text.match(/(\d+)\s*$/);
      values.push({
        id: String(i),
        label: size[1].replace(/\s+/g, ' ').toUpperCase(),
        count: count ? Number(count[1]) : 0,
        // Opaque to the server, handed back inside the rung it belongs to:
        // our index into `boxes`.
        input: String(i),
      });
    }
    return { values: values, boxes: boxes };
  }

  /** Are there at least two systems here worth reconciling? */
  function mixesSystems(values) {
    var seen = {};
    for (var i = 0; i < values.length; i++) seen[values[i].label.slice(0, 2)] = true;
    return Object.keys(seen).length >= 2;
  }

  function build(group, grouping, boxes) {
    var systems = grouping.systems.filter(function (s) { return SYSTEM_NAMES[s]; });
    if (systems.length < 2) return false;

    var content = group.querySelector('.tru-filter-group__content');
    var source = content && content.querySelector('.tru-filter-list');
    if (!source) return false;

    var active = systems.indexOf(homeSystem()) >= 0 ? homeSystem() : systems[0];

    var wrap = document.createElement('div');
    wrap.className = 'tc-size-tabs';

    var tabs = document.createElement('div');
    tabs.className = 'tc-size-tabs__row';
    tabs.setAttribute('role', 'tablist');
    wrap.appendChild(tabs);

    var rungList = document.createElement('ul');
    rungList.className = 'tc-size-rungs';
    rungList.setAttribute('role', 'list');
    wrap.appendChild(rungList);

    var rows = [];

    function paint() {
      for (var i = 0; i < rows.length; i++) {
        var named = rows[i].rung[active];
        /*
         * A tab shows the sizes that exist in that system, and nothing else.
         *
         * These rungs used to fall back to whatever name they did have, so
         * the UK tab listed an "EU 35" between UK 2.5 and UK 3 — a row a
         * British shopper cannot act on, in the one list where every row is
         * supposed to be a size they know. The shop simply has no UK name for
         * that shoe; the honest thing is to leave it out here and show it on
         * the EU tab, where it does have a name.
         *
         * Nothing becomes unreachable: a size missing from one tab is present
         * on the tab of the system that names it, which is what the tabs are
         * for. The one exception is a rung already ticked — hiding a filter
         * that is in force would leave no way to turn it off.
         */
        var ticked = rows[i].box.checked;
        rows[i].li.hidden = !named && !ticked;
        rows[i].text.textContent =
          named || rows[i].rung.uk || rows[i].rung.eu || rows[i].rung.us || '';
        rows[i].li.classList.toggle('is-borrowed', !named);
      }
      var buttons = tabs.querySelectorAll('button');
      for (var j = 0; j < buttons.length; j++) {
        var on = buttons[j].getAttribute('data-system') === active;
        buttons[j].setAttribute('aria-selected', on ? 'true' : 'false');
        buttons[j].classList.toggle('is-on', on);
      }
    }

    systems.forEach(function (system) {
      var button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('role', 'tab');
      button.setAttribute('data-system', system);
      button.textContent = SYSTEM_NAMES[system];
      button.addEventListener('click', function () {
        active = system;
        paint();
      });
      tabs.appendChild(button);
    });

    grouping.rungs.forEach(function (rung) {
      var mine = rung.inputs
        .map(function (index) { return boxes[Number(index)]; })
        .filter(Boolean);
      if (!mine.length) return;

      var li = document.createElement('li');
      var label = document.createElement('label');
      label.className = 'tru-filter-option tc-size-rung';

      var box = document.createElement('input');
      box.type = 'checkbox';
      /* Deliberately without data-tru-filter-input: this is a control, not a
         field. The form keeps submitting the originals, and the section's
         delegated listener must not fire twice for a single tick. */
      box.checked = mine.some(function (b) { return b.checked; });
      box.disabled = mine.every(function (b) { return b.disabled; });
      box.addEventListener('change', function () {
        for (var i = 0; i < mine.length; i++) mine[i].checked = box.checked;
        /* One event, from a real field, so the section applies this exactly
           as it would any other tick — including waiting for Apply on a
           phone rather than reloading under the shopper's thumb. */
        mine[0].dispatchEvent(new Event('change', { bubbles: true }));
        // Unticking a borrowed rung is what makes it fit to hide again.
        paint();
      });

      var text = document.createElement('span');
      text.className = 'tc-size-rung__label';

      var count = document.createElement('span');
      count.className = 'tc-size-rung__count';
      count.textContent = rung.count ? String(rung.count) : '';

      label.appendChild(box);
      label.appendChild(text);
      label.appendChild(count);
      li.appendChild(label);
      rungList.appendChild(li);
      rows.push({ li: li, text: text, rung: rung, box: box });
    });

    if (!rows.length) return false;

    var note = document.createElement('p');
    note.className = 'tc-size-tabs__note';
    note.textContent = 'Sizes are matched across systems, so a UK 9 also finds the pairs labelled EU 43.';
    wrap.appendChild(note);

    content.insertBefore(wrap, source);
    group.classList.add('tc-size-tabs-on');
    paint();
    return true;
  }

  function run(root) {
    var group = findSizeGroup(root);
    if (!group) return;

    var read = readValues(group);
    if (read.values.length < 4 || !mixesSystems(read.values)) return;

    // Claimed before the request, so a swap mid-flight does not start a second.
    group.setAttribute('data-tc-size-tabs', '');

    fetchGrouping(read.values).then(function (grouping) {
      if (!grouping || !grouping.rungs || !grouping.rungs.length) return;
      try {
        build(group, grouping, read.boxes);
      } catch (err) {
        /* The originals were only hidden, never removed, so failing here
           leaves the shopper with the plain list rather than with nothing. */
        group.classList.remove('tc-size-tabs-on');
        if (window.console) console.warn('[size-tabs]', err);
      }
    });
  }

  function start() {
    var root = document.querySelector('[id^="tru-products-"]');
    if (!root) return;
    run(root);
    /* The panel and the grid are replaced wholesale on every filter change,
       which takes these tabs with them. Watching is simpler than asking the
       section to call out, and it covers the back button for free. */
    new MutationObserver(function () { run(root); }).observe(root, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
